// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableRow, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface PopsObj {
    show: boolean;
    title: string;
}
export interface RecentQuoteTableProperties {
    quoteNumber: PopsObj;
    type: PopsObj;
    status: PopsObj;
    requested: PopsObj;
    shipTo: PopsObj;
    expires: PopsObj;
    salesRep: PopsObj;
    user: PopsObj;
    customer: PopsObj;
}

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    // XNG-Changes
    tableProperties: RecentQuoteTableProperties;
}

const mapStateToProps = (state: ApplicationState) => ({
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataCells?: DataTableCellProps;
    shiptoCell?: DataTableCellProps;
    clickableCell?: LinkPresentationProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: {
        gap: 5,
    },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, extendedStyles, tableProperties }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    return (
        <DataTableRow data-test-selector="recentQuotes">
            {tableProperties.quoteNumber.show && (
                <DataTableCell {...styles.clickableCell} {...{ header: tableProperties.quoteNumber.title }}>
                    <RfqQuoteDetailsPageTypeLink title={quote.quoteNumber} quoteId={quote.id} />
                </DataTableCell>
            )}
            {tableProperties.type.show && quoteSettings.jobQuoteEnabled && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.type.title }}>
                    {quote.typeDisplay}
                </DataTableCell>
            )}
            {tableProperties.status.show && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.status.title }}>
                    {quote.statusDisplay}
                </DataTableCell>
            )}
            {tableProperties.requested.show && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.requested.title }}>
                    {orderDateDisplay}
                </DataTableCell>
            )}
            {tableProperties.expires.show && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.expires.title }}>
                    {expirationDateDisplay}
                </DataTableCell>
            )}
            {session.isSalesPerson && (
                <>
                    {tableProperties.salesRep.show && (
                        <DataTableCell {...styles.dataCells} {...{ header: tableProperties.salesRep.title }}>
                            {quote.salespersonName}
                        </DataTableCell>
                    )}
                    {tableProperties.user.show && (
                        <DataTableCell
                            {...styles.dataCells}
                            data-test-selector="user"
                            {...{ header: tableProperties.user.title }}
                        >
                            {quote.userName}
                        </DataTableCell>
                    )}
                </>
            )}
            {tableProperties.customer.show && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.customer.title }}>
                    {customerDisplay}
                </DataTableCell>
            )}

            {!session.isSalesPerson && tableProperties.shipTo.show && (
                <DataTableCell {...styles.shiptoCell} {...{ header: tableProperties.shipTo.title }}>
                    {quote.shipToFullAddress}
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(QuoteSummaryCard);
